import { useEffect, useState } from 'react';
import { documentationExcludedPaymentMethodTags, paymentMethods } from 'consts/paymentMethods';
import { getPotentialPaymentMethods } from 'features/paymentMethods/api';
import { paymentMethodCatalog } from './content';

export type PotentialPaymentMethod = {
    readonly name: string;
    readonly tag: string;
    readonly countries: readonly string[];
    readonly currencies: readonly string[];
};

type ComposedPaymentMethod = {
    readonly tag: string | number;
    readonly forCountries: string | null;
    readonly forCurrencies: string | null;
    readonly pki: null;
};

const splitOptions = (value?: string | readonly string[] | null) =>
    (Array.isArray(value) ? value : String(value ?? '').split(','))
        .map(option => option.trim())
        .filter(Boolean);

const getResources = (payload: any): ComposedPaymentMethod[] => {
    const data = payload?.data?.data ?? payload?.data ?? payload;
    return Array.isArray(data) ? data : [];
};

export const normalizePotentialPaymentMethods = (resources: ComposedPaymentMethod[]) => {
    const methods = new Map<string, {
        name: string;
        countries: Set<string>;
        currencies: Set<string>;
    }>();

    resources.forEach(resource => {
        const tag = String(resource.tag ?? '').trim();
        const normalizedTag = tag.toLowerCase();
        if (!tag || documentationExcludedPaymentMethodTags.has(normalizedTag)) return;

        const current = methods.get(normalizedTag) ?? {
            name: paymentMethods.get(normalizedTag) ?? tag,
            countries: new Set<string>(),
            currencies: new Set<string>()
        };

        splitOptions(resource.forCountries)
            .forEach(country => current.countries.add(country));
        splitOptions(resource.forCurrencies)
            .forEach(currency => current.currencies.add(currency));
        methods.set(normalizedTag, current);
    });

    return Array.from(methods, ([tag, method]) => ({
        tag,
        name: method.name,
        countries: Array.from(method.countries).sort(),
        currencies: Array.from(method.currencies).sort()
    })).sort((first, second) => first.name.localeCompare(second.name));
};

export const fallbackPotentialPaymentMethods: PotentialPaymentMethod[] = paymentMethodCatalog
    .filter(method => !documentationExcludedPaymentMethodTags.has(method.tag.toLowerCase()))
    .map(method => ({
        name: method.name,
        tag: method.tag,
        countries: [...method.countries],
        currencies: [...method.currencies]
    }))
    .sort((first, second) => first.name.localeCompare(second.name));

export default function usePotentialPaymentMethods() {
    const [methods, setMethods] = useState<PotentialPaymentMethod[]>([]);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);

        getPotentialPaymentMethods('?perPage=500')
            .then(async response => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const payload = await response.json();
                if (payload?.success === false) {
                    throw new Error(payload.message || `API error ${payload.code ?? 'unknown'}`);
                }
                if (isMounted) {
                    const methods = normalizePotentialPaymentMethods(getResources(payload));
                    setMethods(methods.length ? methods : fallbackPotentialPaymentMethods);
                }
            })
            .catch(requestError => {
                if (isMounted) {
                    const details = requestError instanceof Error ? requestError.message : 'Unknown error';
                    setError(`Payment methods could not be loaded: ${details}`);
                    setMethods(fallbackPotentialPaymentMethods);
                }
            })
            .finally(() => {
                if (isMounted) setLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, []);

    return { methods, isLoading, error };
}
